<template>
    <div>
        <report-header
            :title="reportName"
            :isShowDateRange="isReportShowDateRange && !isInitialLoad"
            :formatDateRangeTitle="`(${periodStart} - ${periodEnd})`"
            :description="reportDesc"
        />

        <b-card>
            <div id="report-body" class="w-100">
                <div v-if="isInitialLoad" class="w-100 pt-2 pb-2">
                    <i
                        >Please select the
                        <a @click="openFilterModal" class="text-link">filter</a>
                    </i>
                </div>
                <QTable
                    v-else
                    :columns="tableColumn"
                    :data="tableData"
                    :hide-header="hideHeader"
                    hide-bottom
                    :loading="isLoading"
                    class="mb-4"
                    :rows-per-page-options="[0]"
                    row-key="id"
                >
                    <template
                        v-slot:bottom-row
                        v-if="
                            !tableData || (tableData.length == 0 && !isLoading)
                        "
                    >
                        <q-tr>
                            <q-td colspan="8">No records available</q-td>
                        </q-tr>
                    </template>
                    <template v-slot:body-cell="{ value, col }">
                        <q-td :class="col.__tdClass" :style="col.style">
                            <span class="newline-text" v-html="value"></span>
                        </q-td>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>

<script>
import { QTable } from "quasar";
import { debounce } from "lodash";
import ReportService from "@/services/report";
import "@/css/quasar-table.css";
import Report from "@/mixins/report.js";

export default {
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: moment(),
                    end: moment()
                };
            }
        }
    },
    data() {
        return {
            isInitialLoad: true,
            tableColumn: [],
            tableData: [],
            reportName: "",
            reportDesc: "",
            isReportShowDateRange: false,
            startTime: "",
            endTime: "",
            isLoading: false,
            hideHeader: false
        };
    },
    computed: {
        timestamp() {
            return moment
                .tz(this._timeZone)
                .format(this.getDateFormat() + " hh:mm A");
        },
        periodStart() {
            if (this.isReportShowDateRange) {
                return moment
                    .unix(this.startTime)
                    .tz(this._timeZone)
                    .format(this.getDateFormat());
            }
            return null;
        },
        periodEnd() {
            if (this.isReportShowDateRange) {
                return moment
                    .unix(this.endTime)
                    .tz(this._timeZone)
                    .format(this.getDateFormat());
            }
            return null;
        }
    },
    async mounted() {
        let self = this;
        // --- toggle navigation options
        self.defaultFilterDateRange();
        self.data.exportOptionCsvOnly = true;
        self.data.isHideActions = true;
        self.data.refreshOption = false;
        self.data.isCustomReport = true;

        await self._getSetting();
        self.data.datePickerOptionDefault = 1; // Custom range
        self.data.start = moment().tz(self._timeZone).startOf("day");
        self.data.end = moment().tz(self._timeZone).endOf("day");
        self.data.from = moment().tz(self._timeZone).startOf("day").unix();
        self.data.to = moment().tz(self._timeZone).endOf("day").unix();
        self.fetchInitialData();

        /**
         * TODO: to check why this event is triggered multiple times when loading custom report
         * once identified, debounce method can be removed
         */
        self.$root.$on("report-filter-date-range:refresh", function (params) {
            self.onRefresh(params, self);
        });

        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        let self = this;
        self.$root.$off("report-filter-date-range:refresh", self.fetchData);
        self.data.isShowDateRange = true;
        self.data.filterDateOption = true;
        self.data.exportOptionCsvOnly = false;
        self.data.isHideActions = false;
    },
    methods: {
        async fetchInitialData() {
            // To fetch the details of the custom report before render data or open the filter date modal
            let self = this;
            self.isLoading = true;
            self.fetchRecords({
                is_initial_load: 1,
                from: self.data.from,
                to: self.data.to
            }).then(res => {
                self.reportName = res.resource.name;
                self.reportDesc = res.resource.description;
                self.isReportShowDateRange =
                    res.resource.filter_options.date_range;
                self.hideHeader = res.resource.hide_header;

                self.startTime = parseInt(
                    res.resource.filter_options.startTime
                );
                self.endTime = parseInt(res.resource.filter_options.endTime);
                self.data.start = moment
                    .unix(self.startTime)
                    .tz(self._timeZone);
                self.data.from = moment.unix(self.startTime).tz(self._timeZone);
                self.data.end = moment.unix(self.endTime).tz(self._timeZone);
                self.data.to = moment.unix(self.endTime).tz(self._timeZone);

                if (self.isReportShowDateRange) {
                    self.data.isShowDateRange = true;
                    self.data.filterDateOption = true;
                    self.openFilterModal(); // The report has filter date button then will open the filter date modal before render the data
                } else {
                    self.data.isShowDateRange = false;
                    self.data.filterDateOption = false;
                    self.fetchData(); //  The report dont have filter date button hence render the data
                }
            });
        },
        onRefresh: debounce(async (params, self) => {
            self.resetData();
            self.fetchData(params);
        }, 400),
        resetData() {
            let self = this;
            self.tableColumn = [];
            self.tableData = [];
            self.startTime = "";
            self.endTime = "";
            self.hideHeader = false;
        },
        async fetchData(params) {
            let self = this;
            self.isInitialLoad = false;
            self.isLoading = true;
            self.startTime = params ? parseInt(params.from) : "";
            self.endTime = params ? parseInt(params.to) : "";
            await self
                .fetchRecords(params)
                .then(res => {
                    if (res.message.code === 200) {
                        self.tableData = res.resource.data;
                        self.header = res.resource.header;

                        if (self.header && self.header.length > 0) {
                            // reset tableColumn to avoid redundant column when user tries to load
                            // multiple times on slower network
                            self.tableColumn = [];

                            self.header.forEach(element => {
                                self.tableColumn.push({
                                    name: element,
                                    field: element,
                                    label: this.customLabel(element),
                                    align: "left",
                                    headerStyle:
                                        "font-weight:bold; white-space: nowrap;"
                                });
                            });
                        }

                        self.isLoading = false;
                        self.data.isHideActions = false;
                    } else {
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    }
                })
                .catch(e => {
                    console.error(e);
                    self._message("Error: Unable to get the report", "danger");
                });
        },
        fetchRecords(params) {
            let id = this.$route.params.id;
            return ReportService.getRenderCustomReport(id, params);
        },
        customLabel(string) {
            // Replace non-alphanumeric characters except for parentheses and apostrophes
            string = string.replace(/[^a-zA-Z0-9\s()']/g, ' ')
                           .trim()
                           .replace(/\s+/g, ' ') // Normalize multiple spaces
                           .replace(/\s*\(\s*/g, ' (').replace(/\s*\)\s*/g, ') ') // Fix spaces around parentheses
                           .replace(/'/g, ''); // Remove apostrophes

            // Remove nested parentheses
            while (/\([^(]*\([^)]*\)[^)]*\)/.test(string)) {
                string = string.replace(/\(([^()]*)\)/g, '$1'); // Remove inner parentheses
            }

            // Remove empty parentheses
            string = string.replace(/\(\s*\)/g, ''); 

            // Split words outside parentheses (helloWorld -> Hello World)
            string = string.replace(/(\([^()]*\))|[a-zA-Z0-9]+/g, match => {

                // Keep parentheses content unchanged
                if (match.startsWith('(')) return match; 

                // Fix camelCase (e.g., "camelCase" -> "Camel Case") by adding spaces
                match = match.replace(/([a-z])([A-Z])/g, '$1 $2');

                // Fix cases where multiple uppercase letters appear together (e.g., "ATest" -> "A Test")
                match = match.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

                return match;
            });

            // Capitalize words outside parentheses
            string = string.replace(/(\([^()]*\))|[a-zA-Z0-9]+/g, match =>
                match.startsWith('(') ? match : /^[A-Z0-9]+$/.test(match) ? match : match[0].toUpperCase() + match.slice(1).toLowerCase()
            );

            // Add space between letters and numbers outside parentheses
            string = string.replace(/([a-zA-Z])(\d)|(\d)([a-zA-Z])/g, '$1$3 $2$4');

            // Fix spaces around parentheses
            return string.replace(/\s+\(/g, ' (')
                         .replace(/\)\s+/g, ') ')
                         .replace(/\(\s+/g, '(')
                         .replace(/\s+\)/g, ')')
                         .trim();
        }
    }
};
</script>

<style scoped>
.report-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 5px;
}
.report-heading .period {
    font-size: 0.7em;
}
th {
    font-weight: bold;
    white-space: nowrap;
}
.card-body {
    padding-top: 0;
    padding-bottom: 0;
}
.report-description {
    display: block;
    margin-bottom: 10px;
    font-style: italic;
}
.total {
    font-weight: bold;
    border-top: 3px double #e4e4e4;
}
.newline-text {
    white-space: pre-line;
}
</style>
